import { observer } from 'mobx-react-lite';
import {
  Accordion,
  Button,
  CheckboxField,
  FieldGroup,
  showMessageBox,
  TextField,
} from '../../../../../components';
import { ProductHelp } from '../../../../../store/constants/help';
import Product from '../../../store/Product';
import Version from '../store/Version';
import FilesView from './FilesView';
import PreviousFiles from './PreviousFiles';
import classes from './VersionView.module.scss';

export default observer(function VersionView({
  product,
  version,
}: {
  product: Product;
  version: Version;
}) {
  async function onDeleteVersion() {
    const buttonId = await showMessageBox({
      title: 'Delete version',
      content: 'Are you sure you want to delete this version?',
      buttons: [
        { id: 'delete', title: 'Delete', icon: 'trash' },
        { id: 'cancel', title: 'Cancel' },
      ],
    });

    if (buttonId === 'delete') {
      await version.delete();
    }
  }

  return (
    <Accordion
      className={classes.versionBox}
      defaultExpanded={version.isLatest}
      title={`Version ${version.name}${version.isTest ? ' (test)' : ''}`}
    >
      <FieldGroup>
        <TextField
          errorText={version.errors.name}
          label="Version"
          name="name"
          onBlur={version.handleBlur}
          onChange={version.handleChange}
          sx={{ width: '30%' }}
          value={version.name}
        />

        <TextField
          label="Changelog"
          multiline
          name="changelog"
          onChange={version.handleChange}
          sx={{ width: '60%' }}
          value={version.changelog}
        />
      </FieldGroup>

      <PreviousFiles files={version.previousFiles} />

      <FilesView product={product} version={version} />

      <CheckboxField
        helperText="Visible only to you and to Pulse admins, not to customers"
        label="Test version"
        name="isTest"
        onChange={version.handleChange}
        value={version.isTest}
      />

      {!version.isNew && version.canBeChanged && version.areFilesChanged && (
        <CheckboxField
          helperText="Prevent marking as an update for existing users"
          label="Do NOT show as Update in Pulse"
          name="keepVersionId"
          onChange={version.handleChange}
          value={version.keepVersionId}
        />
      )}

      {version.errors.files && (
        <div className={classes.filesError}>{version.errors.files}</div>
      )}

      {!version.isNew && version.canBeChanged && (
        <div className={classes.updateHint}>
          {ProductHelp.fileUpdates.versionChanging}
        </div>
      )}

      {!version.isFirst && version.canBeChanged && (
        <div className={classes.updateHint}>
          {ProductHelp.fileUpdates.generalUpdateHint}
        </div>
      )}

      {version.isNew && (
        <Button
          icon="times"
          onClick={version.delete}
          size="small"
          sx={{ mt: 2 }}
          type="secondary"
        >
          Cancel
        </Button>
      )}

      {!version.isNew && (
        <Button
          icon="trash"
          onClick={onDeleteVersion}
          size="small"
          sx={{ mt: 2 }}
          type="secondary"
        >
          Delete
        </Button>
      )}
    </Accordion>
  );
});
